// src/enums.ts
var values = (...v) => Object.freeze(Object.fromEntries(v.map((x) => [x, x])));
var Role = values("SUPER_ADMIN", "TENANT_ADMIN", "DRIVER", "CUSTOMER");
var TenantStatus = values("ACTIVE", "TRIAL", "SUSPENDED", "CANCELLED");
var VehicleStatus = values("ACTIVE", "INACTIVE", "MAINTENANCE");
var VehicleType = values("SEDAN", "SUV", "HATCHBACK", "MINIVAN", "VAN", "PICKUP", "LUXURY", "OTHER");
var FuelType = values("GASOLINE", "DIESEL", "HYBRID", "ELECTRIC", "LPG", "OTHER");
var VehiclePhotoKind = values("MAIN", "FRONT", "SIDE", "REAR", "INTERIOR", "OTHER");
var DocumentStatus = values("VALID", "EXPIRING_SOON", "EXPIRED", "PENDING_VERIFICATION", "REJECTED");
var DocumentSubject = values("DRIVER", "VEHICLE");
var RequestMode = values("NOW", "SCHEDULED", "RECURRING");
var RequestStatus = values("REQUESTED", "INFO_REQUESTED", "QUOTED", "CONFIRMED", "REJECTED", "CANCELLED", "EXPIRED");
var RideStatus = values(
  "REQUESTED",
  "QUOTED",
  "CONFIRMED",
  "DRIVER_ON_THE_WAY",
  "DRIVER_ARRIVED",
  "IN_PROGRESS",
  "COMPLETED",
  "CANCELLED",
  "EXPIRED"
);
var QuoteStatus = values("PENDING", "ACCEPTED", "REJECTED", "EXPIRED", "SUPERSEDED", "WITHDRAWN");
var BillingMode = values("PER_RIDE", "WEEKLY", "MONTHLY", "PACKAGE");
var PaymentStatus = values("PENDING", "REPORTED", "PARTIALLY_PAID", "VERIFIED", "REJECTED", "VOID");
var PaymentMethod = values("CASH", "TRANSFER");
var TransactionType = values("FARE", "TIP", "CHANGE_RETURNED", "ADJUSTMENT");
var TransactionStatus = values("REPORTED", "CONFIRMED", "REJECTED", "VOIDED");
var MessageType = values("TEXT", "IMAGE", "FILE", "PAYMENT_EVIDENCE", "SYSTEM");
var SeriesStatus = values("PENDING_QUOTE", "QUOTED", "ACTIVE", "PAUSED", "CANCELLED", "ENDED");
var OccurrenceStatus = values("SCHEDULED", "RIDE_CREATED", "SKIPPED", "CANCELLED");
var RideLeg = values("SINGLE", "OUTBOUND", "RETURN");
var PushPlatform = values("ANDROID", "IOS", "WEB");
var BlackoutReason = values("MAINTENANCE", "VACATION", "UNAVAILABLE", "OTHER");
var DriverPresence = values("AVAILABLE", "BUSY", "OFFLINE");
var RequestChannel = values("WEB", "APP", "WHATSAPP", "ADMIN");

// src/ride-lifecycle.ts
var RIDE_TRANSITIONS = {
  REQUESTED: ["QUOTED", "CANCELLED", "EXPIRED"],
  QUOTED: ["CONFIRMED", "CANCELLED", "EXPIRED"],
  CONFIRMED: ["DRIVER_ON_THE_WAY", "CANCELLED"],
  DRIVER_ON_THE_WAY: ["DRIVER_ARRIVED", "CANCELLED"],
  DRIVER_ARRIVED: ["IN_PROGRESS", "CANCELLED"],
  IN_PROGRESS: ["COMPLETED"],
  COMPLETED: [],
  CANCELLED: [],
  EXPIRED: []
};
function canTransition(from, to) {
  return RIDE_TRANSITIONS[from].includes(to);
}
var TRACKING_STATUSES = ["DRIVER_ON_THE_WAY", "DRIVER_ARRIVED", "IN_PROGRESS"];
var ACTIVE_RIDE_STATUSES = ["CONFIRMED", ...TRACKING_STATUSES];
var TERMINAL_RIDE_STATUSES = ["COMPLETED", "CANCELLED", "EXPIRED"];
var RIDE_STATUS_LABEL = {
  REQUESTED: "Solicitado",
  QUOTED: "Cotizado",
  CONFIRMED: "Confirmado",
  DRIVER_ON_THE_WAY: "Conductor en camino",
  DRIVER_ARRIVED: "Conductor lleg\xF3",
  IN_PROGRESS: "En curso",
  COMPLETED: "Completado",
  CANCELLED: "Cancelado",
  EXPIRED: "Expirado"
};
var DRIVER_ACTION_FOR = {
  CONFIRMED: { action: "start-pickup", label: "Ir a recoger" },
  DRIVER_ON_THE_WAY: { action: "arrive", label: "Marcar llegada" },
  DRIVER_ARRIVED: { action: "start", label: "Iniciar viaje" },
  IN_PROGRESS: { action: "complete", label: "Finalizar viaje" }
};

// src/money.ts
var DEFAULT_CURRENCY = "DOP";
var MONEY_RE = /^-?\d+(\.\d{1,2})?$/;
function isMoneyString(v) {
  return MONEY_RE.test(v.trim());
}
function toCents(v) {
  const s = v.trim();
  if (!MONEY_RE.test(s)) throw new Error(`Monto inv\xE1lido: ${v}`);
  const negative = s.startsWith("-");
  const [int, frac = ""] = (negative ? s.slice(1) : s).split(".");
  const cents = BigInt(int) * 100n + BigInt((frac + "00").slice(0, 2));
  return negative ? -cents : cents;
}
function fromCents(c) {
  const negative = c < 0n;
  const abs = negative ? -c : c;
  const int = abs / 100n;
  const frac = (abs % 100n).toString().padStart(2, "0");
  return `${negative ? "-" : ""}${int}.${frac}`;
}
function addMoney(...values2) {
  return fromCents(values2.reduce((acc, v) => acc + toCents(v), 0n));
}
function subMoney(a, b) {
  return fromCents(toCents(a) - toCents(b));
}
function formatMoney(v, currency = DEFAULT_CURRENCY) {
  if (v == null || v === "") return "\u2014";
  const s = fromCents(toCents(v));
  const negative = s.startsWith("-");
  const [int, frac] = (negative ? s.slice(1) : s).split(".");
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const symbol = currency === "DOP" ? "RD$" : `${currency} `;
  return `${negative ? "-" : ""}${symbol}${grouped}.${frac}`;
}

// src/constants.ts
var APP_NAME = "TuConductorRD";
var APP_SLOGAN = "Tu conductor personal, a un clic de distancia.";
var DEFAULT_TIMEZONE = "America/Santo_Domingo";
var DEFAULT_LOCALE = "es-DO";
var API_PREFIX = "/api/v1";
var BRAND_COLORS = {
  black: "#111111",
  white: "#FFFFFF",
  red: "#E5252A",
  lightGray: "#F4F5F7",
  darkGray: "#292929"
};
var ANDROID_CHANNEL_NEW_RIDE_REQUESTS = "ride_requests_v2";
var ANDROID_CHANNELS_OBSOLETE = ["new_ride_requests"];
var ANDROID_CHANNEL_RIDE_UPDATES = "ride_updates";
var ANDROID_CHANNEL_MESSAGES = "messages";
var ANDROID_CHANNEL_REMINDERS = "reminders";
var SocketEvents = {
  // servidor → cliente
  notification: "notification",
  rideRequestUpdated: "ride-request.updated",
  quoteUpdated: "quote.updated",
  rideUpdated: "ride.updated",
  rideLocation: "ride.location",
  chatMessage: "chat.message",
  chatTyping: "chat.typing",
  chatRead: "chat.read",
  replay: "events.replay",
  // cliente → servidor
  joinRide: "c:ride.join",
  leaveRide: "c:ride.leave",
  joinConversation: "c:conversation.join",
  sendLocation: "c:ride.location",
  typing: "c:chat.typing",
  markRead: "c:chat.read",
  resume: "c:events.resume"
};
var MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
var IMAGE_MIME_TYPES = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"];
var DOCUMENT_MIME_TYPES = [...IMAGE_MIME_TYPES, "application/pdf"];

// src/schemas.ts
import { z } from "zod";
var phoneSchema = z.string().trim().regex(/^\+[1-9]\d{7,14}$/, "Usa formato internacional, por ejemplo +18095551234");
var emailSchema = z.string().trim().toLowerCase().email("Correo inv\xE1lido");
var passwordSchema = z.string().min(10, "M\xEDnimo 10 caracteres").max(128).regex(/[A-Za-z]/, "Debe contener letras").regex(/\d/, "Debe contener n\xFAmeros");
var moneySchema = z.string().trim().regex(/^\d+(\.\d{1,2})?$/, "Monto inv\xE1lido");
var registerSchema = z.object({
  firstName: z.string().trim().min(2).max(60),
  lastName: z.string().trim().min(2).max(60),
  email: emailSchema,
  phone: phoneSchema,
  password: passwordSchema,
  acceptTerms: z.literal(true, { message: "Debes aceptar los t\xE9rminos y la pol\xEDtica de privacidad" })
});
var loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, "Requerido")
});
var placeSchema = z.object({
  address: z.string().trim().min(3).max(300),
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
  placeId: z.string().max(300).optional()
});
var rideRequestSchema = z.object({
  serviceTypeId: z.string().uuid(),
  mode: z.enum(["NOW", "SCHEDULED"]),
  origin: placeSchema,
  destination: placeSchema,
  stops: z.array(placeSchema).max(5).default([]),
  passengers: z.number().int().min(1).max(50),
  luggage: z.number().int().min(0).max(50).default(0),
  scheduledAt: z.string().datetime({ offset: true }).optional(),
  roundTrip: z.boolean().default(false),
  returnAt: z.string().datetime({ offset: true }).optional(),
  notes: z.string().max(1e3).optional(),
  contactName: z.string().max(120).optional(),
  contactPhone: phoneSchema.optional()
}).refine((v) => v.mode === "NOW" || !!v.scheduledAt, {
  message: "Selecciona fecha y hora",
  path: ["scheduledAt"]
}).refine((v) => !v.roundTrip || !!v.returnAt, {
  message: "Selecciona la hora de regreso",
  path: ["returnAt"]
});

// src/geo.ts
var R = 6371e3;
var toRad = (d) => d * Math.PI / 180;
var toDeg = (r) => r * 180 / Math.PI;
function haversineMeters(a, b) {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}
function bearingDegrees(a, b) {
  const y = Math.sin(toRad(b.lng - a.lng)) * Math.cos(toRad(b.lat));
  const x = Math.cos(toRad(a.lat)) * Math.sin(toRad(b.lat)) - Math.sin(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.cos(toRad(b.lng - a.lng));
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}
function lerpLatLng(a, b, t) {
  const k = Math.max(0, Math.min(1, t));
  return { lat: a.lat + (b.lat - a.lat) * k, lng: a.lng + (b.lng - a.lng) * k };
}
function lerpAngle(a, b, t) {
  const diff = (b - a + 540) % 360 - 180;
  return (a + diff * Math.max(0, Math.min(1, t)) + 360) % 360;
}
function decodePolyline(encoded) {
  const points = [];
  let index = 0;
  let lat = 0;
  let lng = 0;
  while (index < encoded.length) {
    for (const axis of [0, 1]) {
      let result = 0;
      let shift = 0;
      let b;
      do {
        b = encoded.charCodeAt(index++) - 63;
        result |= (b & 31) << shift;
        shift += 5;
      } while (b >= 32);
      const delta = result & 1 ? ~(result >> 1) : result >> 1;
      if (axis === 0) lat += delta;
      else lng += delta;
    }
    points.push({ lat: lat / 1e5, lng: lng / 1e5 });
  }
  return points;
}
function encodePolyline(points) {
  let out = "";
  let prevLat = 0;
  let prevLng = 0;
  const enc = (v) => {
    let n = v < 0 ? ~(v << 1) : v << 1;
    let s = "";
    while (n >= 32) {
      s += String.fromCharCode((32 | n & 31) + 63);
      n >>= 5;
    }
    return s + String.fromCharCode(n + 63);
  };
  for (const p of points) {
    const lat = Math.round(p.lat * 1e5);
    const lng = Math.round(p.lng * 1e5);
    out += enc(lat - prevLat) + enc(lng - prevLng);
    prevLat = lat;
    prevLng = lng;
  }
  return out;
}
function progressAlongRoute(route, p) {
  if (route.length < 2) return { remainingMeters: route[0] ? haversineMeters(p, route[0]) : 0, offRouteMeters: 0 };
  let best = { seg: 0, t: 0, dist: Infinity };
  const kx = Math.cos(toRad(p.lat)) * 111320;
  const ky = 110540;
  for (let i = 0; i < route.length - 1; i++) {
    const a2 = route[i];
    const b2 = route[i + 1];
    const ax = a2.lng * kx, ay = a2.lat * ky, bx = b2.lng * kx, by = b2.lat * ky, px = p.lng * kx, py = p.lat * ky;
    const dx = bx - ax, dy = by - ay;
    const len2 = dx * dx + dy * dy;
    const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / len2));
    const qx = ax + t * dx, qy = ay + t * dy;
    const d = Math.hypot(px - qx, py - qy);
    if (d < best.dist) best = { seg: i, t, dist: d };
  }
  const a = route[best.seg];
  const b = route[best.seg + 1];
  let remaining = haversineMeters(lerpLatLng(a, b, best.t), b);
  for (let i = best.seg + 1; i < route.length - 1; i++) remaining += haversineMeters(route[i], route[i + 1]);
  return { remainingMeters: remaining, offRouteMeters: best.dist };
}
export {
  ACTIVE_RIDE_STATUSES,
  ANDROID_CHANNELS_OBSOLETE,
  ANDROID_CHANNEL_MESSAGES,
  ANDROID_CHANNEL_NEW_RIDE_REQUESTS,
  ANDROID_CHANNEL_REMINDERS,
  ANDROID_CHANNEL_RIDE_UPDATES,
  API_PREFIX,
  APP_NAME,
  APP_SLOGAN,
  BRAND_COLORS,
  BillingMode,
  BlackoutReason,
  DEFAULT_CURRENCY,
  DEFAULT_LOCALE,
  DEFAULT_TIMEZONE,
  DOCUMENT_MIME_TYPES,
  DRIVER_ACTION_FOR,
  DocumentStatus,
  DocumentSubject,
  DriverPresence,
  FuelType,
  IMAGE_MIME_TYPES,
  MAX_UPLOAD_BYTES,
  MessageType,
  OccurrenceStatus,
  PaymentMethod,
  PaymentStatus,
  PushPlatform,
  QuoteStatus,
  RIDE_STATUS_LABEL,
  RIDE_TRANSITIONS,
  RequestChannel,
  RequestMode,
  RequestStatus,
  RideLeg,
  RideStatus,
  Role,
  SeriesStatus,
  SocketEvents,
  TERMINAL_RIDE_STATUSES,
  TRACKING_STATUSES,
  TenantStatus,
  TransactionStatus,
  TransactionType,
  VehiclePhotoKind,
  VehicleStatus,
  VehicleType,
  addMoney,
  bearingDegrees,
  canTransition,
  decodePolyline,
  emailSchema,
  encodePolyline,
  formatMoney,
  fromCents,
  haversineMeters,
  isMoneyString,
  lerpAngle,
  lerpLatLng,
  loginSchema,
  moneySchema,
  passwordSchema,
  phoneSchema,
  placeSchema,
  progressAlongRoute,
  registerSchema,
  rideRequestSchema,
  subMoney,
  toCents
};
//# sourceMappingURL=index.mjs.map